import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import usePageTitle from '../../hooks/usePageTitle';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { axiosInstance } from '../../routes/axiosInstance';
import FormInput from '../../components/FormInput';
import PublicNav from '../../components/PublicNav';
import PublicFooter from '../../components/PublicFooter';

export default function ForgotPassword() {
  usePageTitle('Reset password');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const response = await axiosInstance.post('/user/password-reset', { email });
      if (response.status === 200) {
        setMessage('A password reset link has been sent to your email.');
        setError('');
      }
    } catch (err) {
      setError('Failed to send reset link. Please check your email and try again.');
      setMessage('');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface dark:bg-gray-870 text-slate-900 dark:text-gray-100 flex flex-col">
      <PublicNav/>

      <main className="flex-1 flex items-center justify-center w-full">
        <div className="max-w-md mx-auto px-6 py-16 w-full">
          <div className="text-center">
            <h1 className="text-xl sm:text-2xl font-bold leading-tight tracking-tight text-slate-900 dark:text-white">
              Forgot your <span className="italic text-teal-700 dark:text-teal-400">password</span>?
            </h1>
            <p className="mt-3 text-sm text-slate-600 dark:text-gray-300">
              Enter your email and we'll send you a link to reset it.
            </p>
            <p className="mt-1 text-xs text-slate-500 dark:text-gray-400">
              The link will be sent to the address associated with your account.
            </p>
          </div>

          <div className="mt-10 rounded-2xl bg-surface/70 dark:bg-gray-870/60 backdrop-blur-sm border border-slate-900/10 dark:border-gray-100/10 p-6 sm:p-8">
            <form onSubmit={handleForgotPassword} className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-slate-700 dark:text-gray-300">
                  Email address
                </label>
                <FormInput
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  autoFocus
                  variant="glass"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              {message && (
                <div role="status" aria-live="polite" className="text-sm space-y-1">
                  <p className="text-green-600 dark:text-green-400">{message}</p>
                  <p className="text-slate-500 dark:text-gray-400">
                    If it doesn't arrive within a few minutes, check your spam folder. The link expires after 3 days.
                  </p>
                </div>
              )}
              {error && (
                <p role="alert" aria-live="assertive" className="text-red-600 dark:text-red-400 text-sm">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="flex w-full justify-center items-center rounded-md bg-slate-900 text-white dark:bg-white dark:text-gray-900 px-4 py-2 text-sm font-medium h-10 shadow hover:opacity-90 transition focus:outline-none focus:ring-2 focus:ring-slate-900 dark:focus:ring-white disabled:opacity-60"
              >
                {submitting ? (
                  <svg className="animate-spin h-5 w-5 text-white dark:text-gray-900" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291a7.978 7.978 0 01-1.664-1.415A8.018 8.018 0 012.34 14H.12A9.985 9.985 0 006 19.29V17.29z"></path>
                  </svg>
                ) : (
                  'Send reset link'
                )}
              </button>
            </form>
          </div>

          <div className="mt-6 text-center">
            <Link to="/signin"
                  className="inline-flex items-center gap-1.5 text-sm text-slate-600 dark:text-gray-400 hover:text-slate-900 dark:hover:text-white transition">
              <ArrowLeftIcon className="h-4 w-4"/>
              Back to sign in
            </Link>
          </div>
        </div>
      </main>

      <PublicFooter/>
    </div>
  );
}
